import FormBuilderAPIUrlList from "@shared/components/form-builder/utils/api.js";
import { ENTITY_TYPE_NAMES } from "@shared/utils/constants";
import {
	checkAllEntityFormsSubmitted,
	checkFormCompleted,
} from "./entity-form-utils";
import entityFormAPI from "./entity-form-api";
import axios from "@/axios";


// TODO provide case & current entity data to the EntityFormList constructor
export class EntityForm {
	constructor(entityFormData = null) {
		this.hasFields = false;
		this.isSubmitted = false;
		this.populateForm(entityFormData);
	}
	submit(payload = true) {
		this.isSubmitted = payload;
	}
	populateForm(payload = null) {
		this.formData = payload;
		this.hasFields = !!payload?.sections?.length || false
	}
	updateSectionData(sectionData) {
		const { section_id } = sectionData;
		const updatedSection = this.formData?.sections?.find((el) => el.section_id === section_id);
		// Update entity's form with new values from FormBuilder:
		if (updatedSection) {
			Object.assign(updatedSection, sectionData);
		}
	}
	get isCompleted() {
		return checkFormCompleted(this.formData);
	}
}

/**
 * Class to manage a list of entity forms.
 */
export class EntityFormList {
	constructor({
		entity = null,
		forms = null,
	} = {}) {
		this.list = null; // array of objects with the form payload and form instance
		this.isStored = false; // indicates if the form list is stored in the server
		this.originalFormList = null; // store original form list response
		
		this.initAPI(entity);

		if (forms) {
			this.initFormList(forms);
		}
	}

	// TODO move to the EntityFormAPI class when created (shared/case-creation/CaseCreationFlow/utils/entity-form-api.js)
	// TODO Optionally, add EntityFormAPI here
	/**
	 * Initialize the API URL List for the entity forms.
	 * @param {Object} entityPayload - Payload of the entity to initialize the API.
	 */
	initAPI(entityPayload = null) {
		if (!entityPayload) {
			this.API = null;
		} else {
			const {
				removeBlock: removeBlockEndpoint,
				removeField: removeFieldEndpoint,
			} = new FormBuilderAPIUrlList({
				case_id: entityPayload.case_id,
				entity_id: entityPayload.id,
				form_id: entityPayload.form_id,
			});
			this.API = {
				removeBlock: async (block_id) => await axios.delete(removeBlockEndpoint(block_id)),
				removeField: async (field_id) => await axios.delete(removeFieldEndpoint(field_id)),
			};
		}
		return this;
	}
	

	/**
	 * Actualize the forms data by fetching forms from the server.
	 */
	refetchForms({case_id, entity_id}) {
		if (!this.list?.length || !case_id || !entity_id) return this;
		// refetch all entity forms:
		const refetchPromises = this.list.map((el) => {
			const tenantCheckId = el.payload?.tenant_check_id;
			if (tenantCheckId) {
				return entityFormAPI.fetchEntityForm(
					{
						case_id,
						entity_id,
					},
					{
						tenant_check_id: el.payload.tenant_check_id,
					}
				)
					.then((data) => {
						if (!data) {
							console.warn("No data received for tenant_check_id:", tenantCheckId);
							return;
						}
						this.addFormData(tenantCheckId, data);
					})
					.catch((error) => {
						console.error("Error fetching entity form:", error);
					});
			}
		});
		return Promise.all(refetchPromises)
			.then(() => this)
			.catch((error) => {
				console.error("Error refetching forms:", error);
				return this;
			});
	}

	/**
	 * Set the storage status of the form list.
	 * @param {Boolean} payload - True if the form list is stored, false otherwise.
	 */
	setStored(payload = true) {
		this.isStored = payload;
		return this;
	}

	/**
	 * Initialize the form list with input data.
	 * @param {Object[]} inputData - Array of initial data for the forms.
	 */
	initFormList(inputData) {
		if (!inputData || !Array.isArray(inputData)) {
			throw new Error("Input data must be an array.");
		}
		this.list = inputData.map((el) => ({
			payload: el, // initial data
			form: null, // EntityForm instance
		}));

		return this;
	}

	/**
	 * Add a new form to the list.
	 * @param {Object} entityFormData - Data for the new form.
	 * @param {String} key - Key to identify the form in the list.
	 */
	addFormData(id, entityFormData, key = "tenant_check_id") {
		if (!this.list?.length) {
			throw new Error("Form list is not initialized. Call initFormList first.");
		}

		const form = new EntityForm(entityFormData);
		const currentFormData = this.list.find((el) => el.payload[key] === id);
		if (currentFormData) {
			currentFormData.form = form;
		}

		return this;
	}

	submitForm(id, payload = true, key = "tenant_check_id") {
		if (!this.list?.length) {
			throw new Error("Form list is not initialized. Call initFormList first.");
		}

		const currentFormData = this.list.find((el) => el.payload[key] === id);
		if (currentFormData && currentFormData.form) {
			currentFormData.form.submit(payload);
		}

		return this;
	}

	submitAllForms(payload = true) {
		if (!this.list?.length) return this;

		// submit all entity forms:
		this.list.forEach((el) => {
			if (el.form && !el.form.isSubmitted) {
				el.form.submit(payload);
			}
		});

		return this;
	}

	resetList() {
		this.list = [];
		return this;
	}

	/**
	 * Check if all forms in the list are submitted.
	 * @returns {Boolean} True if all forms are submitted, false otherwise.
	 */
	get isAllSubmitted() {
		if (!this.list || !Array.isArray(this.list) || !this.list.length) {
			// if no forms, then all are submitted:
			return true;
		}
		const entityForms = this.list.map((el) => el.form);
		return checkAllEntityFormsSubmitted(entityForms);
	}
	get isAllStored() {
		if (!this.list || !Array.isArray(this.list) || !this.list.length) {
			// if no forms, then all are completed:
			return true;
		}
		return this.list.every((el) => el.payload?.creation_form_submitted);
	}
	get isAllCompleted() {
		if (!this.list || !Array.isArray(this.list) || !this.list.length) {
			// if no forms, then all are completed:
			return true;
		}
		return this.list.every((el) => el.form?.isCompleted);
	}

	get hasFields() {
		if (!this.list || !Array.isArray(this.list) || !this.list.length) {
			return false;
		}
		return this.list.some((el) => el.form?.hasFields);
	}

	/**
	 * Merges all forms' sections into one form
	 */
	get combinedForm() {
		const formData = this.list.reduce((acc, current) => {
			if (current?.form?.formData?.sections?.length) {
				if (!acc.sections) {
					acc.sections = [];
				}
				acc.sections.push(...current.form.formData.sections);
			}
			return acc;
		}, {});
		return { formData };
	}
}

/**
 * @typedef {Object} CaseEntityData
 * @property {Object[]} case_entity_info
 * @property {Object} case_entity_type_info
 * @property {Object} case_entities
 * 
 * @typedef {Object[]} CaseEntities
 * 
 * @typedef {Object} EntitiesCounterTotal
 * @property {Number} max
 * @property {Number} count
 * @property {Number} remaining
 * 
 * @typedef {Object} EntitiesCounter
 * @property {Object[]} entities
 * @property {EntitiesCounterTotal} total
 * 
 * @param {CaseEntityData} caseEntityData 
 * @param {CaseEntities} caseEntities 
 * @returns {EntitiesCounter} entities result counter
 */
export const getEntitiesCounter = (caseEntityData, caseEntityList) => {
	if (!caseEntityData) return null;

	const {
		case_entity_type_info,
		case_entity_info,
		case_entities,
	} = caseEntityData;

	const caseEntities = caseEntityList ?? case_entities;

	if (!(case_entity_type_info && case_entity_info && caseEntities)) return null;

	const caseTypeId = (Object.entries(case_entity_type_info).find(([, value]) => value.name === ENTITY_TYPE_NAMES.CASE) ?? [])[0];

		// find entity with 'Case' type:
		const caseEntity = caseEntities.find(
			(el) => el.entity_type_id === caseTypeId
		);

		const resultCounter = {};
		// consider minimum counts of other entities
		const totalMinCount = Object.values(case_entity_type_info).reduce(
			(result, current) => {
				return result + current.min;
			},
			0
		);
		let maxEntitiesCount = case_entity_info.max_case_entity_count;
		// consider "Case" entity
		const totalCount = caseEntities.length;
		let totalRemaining = maxEntitiesCount - totalCount;

		resultCounter.entities = Object.entries(case_entity_type_info)
			.filter(([key]) => key !== caseEntity.entity_type_id)
			.reduce((result, [key, entityInfo]) => {
				const otherTotalMinCount = totalMinCount - entityInfo.min;
				result.push({
					...entityInfo,
					id: key,
					max: entityInfo.max,
					calculatedMax: maxEntitiesCount - otherTotalMinCount,
					min: entityInfo.min,
					count: caseEntities.filter((el) => el.entity_type_id === key)
						.length, // consider other entities' min count that must be added to the case
					remaining: Math.min(
						entityInfo.max -
							caseEntities.filter((el) => el.entity_type_id === key)
								.length,
						totalRemaining
					),
				});
				return result;
			}, []);

		resultCounter.total = {
			max: maxEntitiesCount,
			count: totalCount,
			remaining: totalRemaining,
		};

		return resultCounter;
}

/**
 * @typedef {Object} EntityToAdd
 * @property {String} id
 * @property {String} name
 * @property {Object} entity
 * @property {Boolean} disabled
 * 
 * @param {EntitiesCounter} entitiesCounter 
 * @returns {EntityToAdd[]} list of entities to add
 */
export const getEntitiesToAdd = (entitiesCounter) => {
	if (!entitiesCounter?.entities) return [];

	const isTotalMaxReached = entitiesCounter?.total.count >= entitiesCounter?.total.max;
	// uncomment and use if we should consider other entity typesx counts:
	// const isDisabled = el.count >= el.calculatedMax // max hasn't been reached but creation of new entity will lead to invalid entities structure
	return (
		entitiesCounter.entities
			.filter((el) => el.count < el.max) // remove if we need to handle item's visibility within child components
			.map((el) => ({
				id: el.id,
				name: el.name,
				entity: el,
				disabled: isTotalMaxReached, // max hasn't been reached but creation of new entity will lead to invalid entities structure
				// visible: el.count < el.max, // add instead of .filter() if we need to handle item's visibility within child components
			}))
	);
}